import type { ActorLookup } from "@istari-frontend/notifications/model";
import { useMemo } from "react";

import { useResourceMetadata } from "~/resource/feature/use-resource-metadata";
import { useLastResolvedUserDisplayNames } from "~/user/feature/use-last-resolved-user-display-names";
import { useUserDisplayNames } from "~/user/feature/use-user-display-names";

import { toActorLookup, UNKNOWN_ACTOR_LOOKUP } from "./actor-lookup";
import { useReportLookupFailures } from "./use-report-lookup-failures";

export function useShareEnrichment(resourceIds: string[], retainCached = false) {
  const resources = useResourceMetadata(resourceIds);
  const createdByIds = resourceIds.map((id) => {
    const resource = resources.get(id);
    return !retainCached && resource?.isError ? null : resource?.data?.createdById;
  });
  const users = useUserDisplayNames(createdByIds);
  const retained = useLastResolvedUserDisplayNames(users);
  const built = useMemo(
    () => ({
      resourceDetails: resourceIds.map((id) => {
        const resource = resources.get(id);
        const data = !retainCached && resource?.isError ? undefined : resource?.data;
        return {
          name: data?.name ?? null,
          resourceType: data?.resourceType ?? null,
          createdById: data?.createdById ?? null,
        };
      }),
      actorNames: resourceIds.map((id): ActorLookup => {
        const resource = resources.get(id);
        if (!retainCached && resource?.isError) return UNKNOWN_ACTOR_LOOKUP;
        const creatorId = resource?.data?.createdById;
        const user = creatorId ? users[creatorId] : undefined;
        const retainedName = retainCached && creatorId ? retained[creatorId]?.displayName : null;
        if (user?.status !== "resolved" && retainedName) return { status: "resolved", displayName: retainedName };
        return toActorLookup(user, !!resource?.isLoading);
      }),
      isLoading: resourceIds.some((id) => {
        const resource = resources.get(id);
        const creatorId = resource?.data?.createdById;
        const user = creatorId ? users[creatorId] : undefined;
        return resource?.isLoading || user?.status === "loading";
      }),
    }),
    [resources, users, retained, resourceIds, retainCached],
  );
  useReportLookupFailures(
    "notifications-resource-lookup-failed",
    [...resources].flatMap(([resourceId, result]) =>
      !retainCached && result.isError ? [{ error: result.error, context: { resourceId } }] : [],
    ),
  );
  useReportLookupFailures(
    "notifications-actor-lookup-failed",
    Object.entries(users).flatMap(([userId, result]) =>
      !retainCached && result.status === "error" ? [{ error: result.error, context: { userId } }] : [],
    ),
  );
  return built;
}
